import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/lib/shadcn/components/ui/badge';
import { Button } from '@/lib/shadcn/components/ui/button';
import { cn } from '@/lib/shadcn/utils/utils';
import type { CustomerProductPurchase } from '../../models/customerProductPurchase.model';

export type CustomerProductPurchaseListLabels = {
  name: string;
  productId: string;
  amount: string;
  quantity: string;
  purchasedAt: string;
  subscriptionFlag: string;
  invoice: string;
  yes: string;
  no: string;
};

export type CustomerProductPurchaseListProps = {
  purchases: CustomerProductPurchase[];
  labels: CustomerProductPurchaseListLabels;
  formatAmount: (purchase: CustomerProductPurchase) => string;
  formatPurchasedAt: (utc: string) => string;
  formatDisplayName: (name: string) => string;
  className?: string;
};

const TH_CLASS =
  'px-4 py-2.5 text-left text-xs font-medium text-muted-foreground align-top whitespace-nowrap';

const TD_CLASS = 'px-4 py-3.5 align-top text-sm';

function subscriptionBadgeClass(isSubscription: boolean): string {
  return isSubscription
    ? 'border-sky-200 bg-sky-50 text-sky-900'
    : 'border-slate-200 bg-slate-50 text-slate-700';
}

function MobileLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-xs text-muted-foreground sm:hidden">{children}</span>
  );
}

export const CustomerProductPurchaseList: React.FC<
  CustomerProductPurchaseListProps
> = ({
  purchases,
  labels,
  formatAmount,
  formatPurchasedAt,
  formatDisplayName,
  className,
}) => {
  return (
    <div
      className={cn(
        'overflow-x-auto rounded-lg border border-border/60',
        className
      )}
    >
      {/* Mobile: stacked rows */}
      <ul className="divide-y divide-border/60 sm:hidden">
        {purchases.map(purchase => (
          <li key={purchase.Id} className="space-y-2 px-4 py-3.5">
            <div className="flex items-baseline justify-between gap-3">
              <MobileLabel>{labels.name}</MobileLabel>
              <p className="text-sm font-medium text-foreground">
                {formatDisplayName(purchase.DisplayName)}
              </p>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <MobileLabel>{labels.productId}</MobileLabel>
              <p className="font-mono text-xs text-muted-foreground break-all text-right">
                {purchase.StripeProductId || '—'}
              </p>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <MobileLabel>{labels.amount}</MobileLabel>
              <span className="text-sm font-semibold tabular-nums">
                {formatAmount(purchase)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <MobileLabel>{labels.quantity}</MobileLabel>
              <span className="text-sm">{purchase.Quantity}</span>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <MobileLabel>{labels.purchasedAt}</MobileLabel>
              <span className="text-sm text-muted-foreground text-right">
                {purchase.PurchasedAtUtc
                  ? formatPurchasedAt(purchase.PurchasedAtUtc)
                  : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <MobileLabel>{labels.subscriptionFlag}</MobileLabel>
              <Badge
                variant="outline"
                className={cn(
                  'font-normal',
                  subscriptionBadgeClass(purchase.IsSubscriptionProduct)
                )}
              >
                {purchase.IsSubscriptionProduct ? labels.yes : labels.no}
              </Badge>
            </div>
            <div className="flex justify-end pt-1">
              {purchase.InvoiceUrl ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  asChild
                >
                  <a
                    href={purchase.InvoiceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {labels.invoice}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                </Button>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop: table for reliable column alignment */}
      <table className="hidden sm:table w-full min-w-[920px] border-collapse">
        <thead className="border-b border-border/60 bg-muted/40">
          <tr>
            <th className={cn(TH_CLASS, 'w-[11%]')}>{labels.name}</th>
            <th className={cn(TH_CLASS, 'w-[20%]')}>{labels.productId}</th>
            <th className={cn(TH_CLASS, 'w-[10%]')}>{labels.amount}</th>
            <th className={cn(TH_CLASS, 'w-[6%]')}>{labels.quantity}</th>
            <th className={cn(TH_CLASS, 'w-[18%]')}>{labels.purchasedAt}</th>
            <th className={cn(TH_CLASS, 'w-[14%]')}>
              {labels.subscriptionFlag}
            </th>
            <th className={cn(TH_CLASS, 'w-[12%] text-right')}>
              {labels.invoice}
            </th>
          </tr>
        </thead>
        <tbody>
          {purchases.map(purchase => (
            <tr
              key={purchase.Id}
              className="border-b border-border/60 last:border-b-0 transition-colors hover:bg-muted/20"
            >
              <td className={TD_CLASS}>
                <p className="font-medium leading-snug text-foreground">
                  {formatDisplayName(purchase.DisplayName)}
                </p>
              </td>
              <td className={TD_CLASS}>
                <p className="font-mono text-xs text-muted-foreground break-all leading-snug">
                  {purchase.StripeProductId || '—'}
                </p>
              </td>
              <td className={TD_CLASS}>
                <span className="font-semibold tabular-nums text-foreground">
                  {formatAmount(purchase)}
                </span>
              </td>
              <td className={TD_CLASS}>
                <span className="text-foreground">{purchase.Quantity}</span>
              </td>
              <td className={TD_CLASS}>
                <span className="text-muted-foreground">
                  {purchase.PurchasedAtUtc
                    ? formatPurchasedAt(purchase.PurchasedAtUtc)
                    : '—'}
                </span>
              </td>
              <td className={TD_CLASS}>
                <Badge
                  variant="outline"
                  className={cn(
                    'font-normal',
                    subscriptionBadgeClass(purchase.IsSubscriptionProduct)
                  )}
                >
                  {purchase.IsSubscriptionProduct ? labels.yes : labels.no}
                </Badge>
              </td>
              <td className={cn(TD_CLASS, 'text-right')}>
                {purchase.InvoiceUrl ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs"
                    asChild
                  >
                    <a
                      href={purchase.InvoiceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {labels.invoice}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </Button>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
